/**
 * Indicator math + per-strategy styling helpers for TradingChart.
 * Pure functions — no React, no API.
 */

export const STRATEGY_COLORS = {
  Technical: '#8b5cf6',
  Statistical: '#3b82f6',
  Fundamental: '#10b981',
  Options: '#f59e0b',
  Quantitative: '#ec4899',
  Sentiment: '#6366f1',
  Event: '#14b8a6',
  Macro: '#0ea5e9',
  Risk: '#22c55e',
  Specialized: '#a855f7',
  Advanced: '#f97316',
};

export const getStrategyColor = (category) => STRATEGY_COLORS[category] || '#6b7280';

export const calculateMA = (prices, period) => {
  const ma = [];
  for (let i = 0; i < prices.length; i += 1) {
    if (i < period - 1) {
      ma.push(null);
    } else {
      const window = prices.slice(i - period + 1, i + 1);
      const sum = window.reduce((a, b) => a + b, 0);
      ma.push(sum / period);
    }
  }
  return ma;
};

export const calculateBollingerBands = (prices, period, stdDev) => {
  const ma = calculateMA(prices, period);
  const upper = [];
  const lower = [];

  for (let i = 0; i < prices.length; i += 1) {
    if (i < period - 1) {
      upper.push(null);
      lower.push(null);
    } else {
      const slice = prices.slice(i - period + 1, i + 1);
      const avg = ma[i];
      const variance = slice.reduce((sum, price) => sum + (price - avg) ** 2, 0) / period;
      const std = Math.sqrt(variance);
      upper.push(avg + stdDev * std);
      lower.push(avg - stdDev * std);
    }
  }

  return { upper, lower, middle: ma };
};

/**
 * Inspect the strategy name and return three booleans describing which
 * indicator overlays the chart should render. Keeping this string-matching
 * logic in one place means the chart component stays declarative.
 */
export const strategyOverlayFlags = (strategy) => {
  const name = (strategy?.name || '').toLowerCase();
  return {
    hasMA: name.includes('momentum') || name.includes('moving average') || name.includes('ichimoku'),
    hasBB: name.includes('bollinger') || name.includes('reversion'),
    hasSupport: name.includes('breakout') || name.includes('triangle') || name.includes('donchian'),
  };
};

/**
 * Given a series of {date, price, volume} rows, attach the indicator columns
 * appropriate for the strategy. Always returns the same row count.
 */
export const addTechnicalIndicators = (data, strategy) => {
  if (!data?.length) return data;
  const prices = data.map((d) => d.price);
  const { hasMA, hasBB, hasSupport } = strategyOverlayFlags(strategy);

  if (hasMA) {
    const ma20 = calculateMA(prices, 20);
    const ma50 = calculateMA(prices, 50);
    return data.map((d, i) => ({ ...d, ma20: ma20[i], ma50: ma50[i] }));
  }

  if (hasBB) {
    const bb = calculateBollingerBands(prices, 20, 2);
    return data.map((d, i) => ({
      ...d,
      upperBand: bb.upper[i],
      lowerBand: bb.lower[i],
      middleBand: bb.middle[i],
    }));
  }

  if (hasSupport) {
    const recent = prices.slice(-20);
    const high = Math.max(...recent);
    const low = Math.min(...recent);
    return data.map((d) => ({ ...d, resistance: high, support: low }));
  }

  return data;
};
